import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";
import { enforceNotSuspended } from "@/integrations/supabase/suspended-guard";

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

/**
 * HTTP cache headers for repeat-visit speed.
 *
 * - Hashed build assets get long-lived immutable caching — safe because the
 *   filename changes on every deploy.
 * - Public marketing HTML (/, /legal/*) gets a short edge cache with
 *   stale-while-revalidate so repeat visitors get an instant paint while we
 *   revalidate in the background.
 * - Authenticated/unknown HTML stays `private, no-store` to avoid leaking
 *   per-user content into shared caches.
 */
const cacheHeadersMiddleware = createMiddleware().server(async ({ request, next }) => {
  const result = await next();
  const response = (result as { response?: Response }).response;
  if (!(response instanceof Response)) return result;
  if (response.headers.has("cache-control")) return result;

  const url = new URL(request.url);
  const path = url.pathname;
  const contentType = response.headers.get("content-type") ?? "";

  const isHashedAsset =
    /\/(_build|assets)\//.test(path) && /\.[0-9a-f]{8,}\./i.test(path);
  if (isHashedAsset) {
    response.headers.set("cache-control", "public, max-age=31536000, immutable");
    return result;
  }

  // Non-hashed static assets (fonts, images, icons, media) — cache for 30 days
  // with a long SWR window so repeat visits are instant but updates still
  // propagate within a day of a deploy.
  const isStaticAsset =
    /\.(woff2?|ttf|otf|eot|png|jpe?g|gif|webp|avif|svg|ico|bmp|mp4|webm|mp3|ogg|wav)$/i.test(path);
  if (isStaticAsset) {
    response.headers.set(
      "cache-control",
      "public, max-age=2592000, stale-while-revalidate=86400",
    );
    return result;
  }

  const isPublicHtml =
    contentType.includes("text/html") &&
    (path === "/" || path.startsWith("/legal/"));
  if (isPublicHtml) {
    response.headers.set(
      "cache-control",
      "public, max-age=0, s-maxage=300, stale-while-revalidate=86400",
    );
    return result;
  }

  if (contentType.includes("text/html")) {
    response.headers.set("cache-control", "private, no-store");
  }
  return result;
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth, enforceNotSuspended],
  requestMiddleware: [errorMiddleware, cacheHeadersMiddleware],
}));
